import { useMemo } from 'react';
import { Box, Button, Chip, Link, Stack, Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import Iconify from '@/components/Iconify';
import { bookingsManagementPath } from '@/features/bookings-management/routes/paths';
import { returnSessionFilterFromDate } from '@/features/bookings-management/utils/bookingSessions';
import { ROOT_TASK } from '@/features/tasks/routes/paths';
import type { AiMonitorSource } from '../types';

type AiMonitorSessionResult = {
  id: string;
  bookingId?: string;
  date?: string;
  time?: string;
  client?: string;
  address?: string;
  service?: string;
  status?: string;
};

type AiMonitorSessionAnswer = {
  intro: string[];
  sessions: AiMonitorSessionResult[];
  outro: string[];
};

type AiMonitorKpiTone = 'success' | 'warning' | 'error' | 'info';

type AiMonitorKpiMetric = {
  key: string;
  label: string;
  value: string;
  icon: string;
  tone: AiMonitorKpiTone;
};

type AiMonitorKpiAnswer = {
  metrics: AiMonitorKpiMetric[];
  body: string;
};

type AiMonitorTaskResult = {
  id: string;
  title?: string;
  priority?: string;
  assignee?: string;
  dueDate?: string;
  section?: string;
  tags?: string;
  sourceUrl?: string;
  nextAction?: string;
};

type AiMonitorTaskAnswer = {
  intro: string[];
  tasks: AiMonitorTaskResult[];
  outro: string[];
};

type AiMonitorTaskTextField = Exclude<keyof AiMonitorTaskResult, 'id'>;

type AiMonitorPaymentResult = {
  id: string;
  type: 'Payment' | 'Transaction';
  client?: string;
  amount?: string;
  bookingId?: string;
  bookingSessionId?: string;
  bookingSessionDate?: string;
  bookingSessionUrl?: string;
  failureReason?: string;
  attemptDate?: string;
  status?: string;
  sourceUrl?: string;
  nextCollectionStep?: string;
};

type AiMonitorPaymentHeader = AiMonitorPaymentResult & {
  rawDetails?: string;
};

type AiMonitorPaymentAnswer = {
  intro: string[];
  payments: AiMonitorPaymentResult[];
  outro: string[];
};

type AiMonitorActionQueueItem = {
  id: string;
  title: string;
  severity?: string;
  reason?: string;
  linkedSource?: string;
  nextStep?: string;
};

type AiMonitorActionQueueTextField = Exclude<
  keyof AiMonitorActionQueueItem,
  'id'
>;

type AiMonitorActionQueueAnswer = {
  intro: string[];
  actions: AiMonitorActionQueueItem[];
  outro: string[];
};

type AiMonitorMessageContentProps = {
  content: string;
  sources?: AiMonitorSource[];
  inputDisabled?: boolean;
  onSubmitPrompt?: (prompt: string) => void;
};

type DetailItemProps = {
  icon: string;
  label: string;
  value?: string;
};

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const normalizeAnswerLine = (line: string) =>
  line
    .trim()
    .replace(/^[-*]\s*/, '')
    .replace(/^\d+[.)]\s*/, '')
    .trim();

const normalizeValue = (value: string) => value.trim().replace(/\s+/g, ' ');

const unwrapJsonFence = (value: string) =>
  value.trim().match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i)?.[1] ?? value.trim();

const normalizeEscapedAnswerText = (value: string) =>
  value.includes('\\n') ? value.replace(/\\n/g, '\n') : value;

export const normalizeAiMonitorAnswerContent = (content: string) => {
  let normalizedContent = unwrapJsonFence(content);

  for (let index = 0; index < 2; index += 1) {
    try {
      const parsedContent = JSON.parse(normalizedContent) as unknown;

      if (typeof parsedContent === 'string') {
        normalizedContent = unwrapJsonFence(parsedContent);
        continue;
      }

      if (parsedContent && typeof parsedContent === 'object') {
        const candidate = parsedContent as Record<string, unknown>;
        const answerValue =
          candidate.answer ??
          candidate.content ??
          candidate.message ??
          candidate.text ??
          candidate.output;

        if (typeof answerValue === 'string') {
          normalizedContent = unwrapJsonFence(answerValue);
          continue;
        }
      }
    } catch {
      break;
    }

    break;
  }

  return normalizeEscapedAnswerText(normalizedContent).trim();
};

const KPI_VALUE_PATTERN =
  '([$\\u00a3\\u20ac]?\\s*\\d[\\d,]*(?:\\.\\d+)?(?:\\s*[kKmM])?)';

const KPI_DEFINITIONS: Array<
  Omit<AiMonitorKpiMetric, 'value'> & { labelPattern: string }
> = [
  {
    key: 'completedSessions',
    label: 'Completed',
    labelPattern: '(?:completed|done)\\s+sessions?',
    icon: 'eva:checkmark-circle-2-outline',
    tone: 'success',
  },
  {
    key: 'cancelledSessions',
    label: 'Cancelled',
    labelPattern: '(?:cancelled|canceled)(?:\\s+sessions?)?',
    icon: 'eva:close-circle-outline',
    tone: 'error',
  },
  {
    key: 'unassignedSessions',
    label: 'Unassigned',
    labelPattern: 'unassigned(?:\\s+sessions?)?',
    icon: 'eva:person-delete-outline',
    tone: 'warning',
  },
  {
    key: 'unpaidRevenue',
    label: 'Unpaid',
    labelPattern:
      '(?:unpaid(?:\\s+(?:revenue|payments?|amount|balance))?|(?:revenue|payments?|amount|balance)\\s+unpaid)',
    icon: 'eva:credit-card-outline',
    tone: 'warning',
  },
  {
    key: 'urgentTasks',
    label: 'Urgent tasks',
    labelPattern: 'urgent\\s+tasks?',
    icon: 'eva:alert-triangle-outline',
    tone: 'error',
  },
  {
    key: 'openIssues',
    label: 'Open issues',
    labelPattern: 'open\\s+issues?',
    icon: 'eva:alert-circle-outline',
    tone: 'info',
  },
];

const normalizeKpiValue = (value: string) =>
  normalizeValue(value).replace(/^([$\u00a3\u20ac])\s+/, '$1');

const getFirstNumericValue = (value: string) => value.match(/\d+/)?.[0] ?? '';

const getIsoDateValue = (value?: string) =>
  value?.match(/\d{4}-\d{2}-\d{2}/)?.[0];

const getTaskIdValue = (value?: string | number | null) =>
  String(value ?? '').match(/\d+/)?.[0] ?? '';

const getTaskRouteUrl = (value?: string | number | null) => {
  const taskId = getTaskIdValue(value);

  return taskId ? `${ROOT_TASK}/gsc-${taskId}` : null;
};

const isBookingSessionUrl = (url?: string | null) =>
  Boolean(
    url &&
      (url.includes('/booking-sessions/') ||
        url.includes('sessionID=') ||
        url.includes('sessionId='))
  );

const getKpiValueFromText = (
  text: string,
  definition: (typeof KPI_DEFINITIONS)[number]
) => {
  const labelPattern = `(?:${definition.labelPattern})`;
  const valueBeforeLabel = new RegExp(
    `${KPI_VALUE_PATTERN}\\s+${labelPattern}`,
    'i'
  );
  const labelBeforeValue = new RegExp(
    `${labelPattern}\\s*(?::|=|-|is|are)?\\s*${KPI_VALUE_PATTERN}`,
    'i'
  );
  const valueBeforeMatch = text.match(valueBeforeLabel);
  const labelBeforeMatch = text.match(labelBeforeValue);

  if (valueBeforeMatch?.[1]) {
    return normalizeKpiValue(valueBeforeMatch[1]);
  }

  if (labelBeforeMatch?.[1]) {
    return normalizeKpiValue(labelBeforeMatch[1]);
  }

  return null;
};

const getKpiMetricCountInLine = (line: string) =>
  KPI_DEFINITIONS.filter((definition) => getKpiValueFromText(line, definition))
    .length;

const isKpiSummaryLine = (line: string) => {
  const trimmedLine = line.trim();
  const normalizedLine = normalizeAnswerLine(line).toLowerCase();
  const metricCount = getKpiMetricCountInLine(line);

  if (/^kpi summary:?$/i.test(normalizedLine)) return true;
  if (metricCount >= 2) return true;

  return (
    metricCount > 0 &&
    (/^[-*]\s+/.test(trimmedLine) ||
      /^(kpi summary|summary|overview)\b/.test(normalizedLine))
  );
};

export const parseAiMonitorKpiAnswer = (
  content: string
): AiMonitorKpiAnswer | null => {
  const metrics = KPI_DEFINITIONS.map((definition) => {
    const value = getKpiValueFromText(content, definition);

    if (!value) return null;

    return {
      key: definition.key,
      label: definition.label,
      icon: definition.icon,
      tone: definition.tone,
      value,
    };
  }).filter((metric): metric is AiMonitorKpiMetric => Boolean(metric));

  if (metrics.length < 2) return null;

  const body = content
    .split(/\r?\n/)
    .filter((line) => !isKpiSummaryLine(line))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return { metrics, body };
};

const parseAiMonitorTaskHeader = (line: string) => {
  const normalizedLine = normalizeAnswerLine(line);
  const taskMatch = normalizedLine.match(
    /^Task(?:\s+ID)?\s*:?\s*#?([A-Za-z0-9-]*\d[A-Za-z0-9-]*)\b/i
  );
  const routeReferenceMatch = normalizedLine.match(/^(GSC-\d+)\b/i);

  return taskMatch?.[1] ?? routeReferenceMatch?.[1] ?? null;
};

const isTaskPlaceholderHeader = (line: string) =>
  /^Task\s+ID:?$/i.test(normalizeAnswerLine(line));

const TASK_DETAIL_LABELS = new Set([
  'task id',
  'id',
  'title',
  'title/location',
  'location',
  'priority',
  'assignee',
  'due date',
  'due',
  'section',
  'column',
  'tags',
  'source link',
  'source',
  'next action needed',
  'next action',
]);

const isTaskDetailLabel = (label: string) =>
  TASK_DETAIL_LABELS.has(label.toLowerCase().trim());

const setTaskField = (
  task: AiMonitorTaskResult,
  label: string,
  value: string
): AiMonitorTaskTextField | null => {
  const normalizedLabel = label.toLowerCase().trim();
  const normalizedValue = normalizeValue(value).replace(/\.$/, '');

  if (normalizedLabel === 'task id' || normalizedLabel === 'id') {
    const taskId = normalizedValue.match(/[A-Za-z0-9-]*\d[A-Za-z0-9-]*/)?.[0];

    if (taskId) {
      task.id = taskId;
    }

    return null;
  }

  if (
    normalizedLabel === 'title' ||
    normalizedLabel === 'title/location' ||
    normalizedLabel === 'location'
  ) {
    task.title = normalizedValue;
    return 'title';
  }

  if (normalizedLabel === 'priority') {
    task.priority = normalizedValue;
    return 'priority';
  }

  if (normalizedLabel === 'assignee') {
    task.assignee = normalizedValue;
    return 'assignee';
  }

  if (normalizedLabel === 'due date' || normalizedLabel === 'due') {
    task.dueDate = normalizedValue;
    return 'dueDate';
  }

  if (normalizedLabel === 'section' || normalizedLabel === 'column') {
    task.section = normalizedValue;
    return 'section';
  }

  if (normalizedLabel === 'tags') {
    task.tags = normalizedValue;
    return 'tags';
  }

  if (normalizedLabel === 'source link' || normalizedLabel === 'source') {
    task.sourceUrl = normalizedValue;
    return 'sourceUrl';
  }

  if (
    normalizedLabel === 'next action needed' ||
    normalizedLabel === 'next action'
  ) {
    task.nextAction = normalizedValue;
    return 'nextAction';
  }

  return null;
};

const appendTaskField = (
  task: AiMonitorTaskResult,
  field: AiMonitorTaskTextField,
  value: string
) => {
  const normalizedValue = normalizeAnswerLine(value).replace(/\.$/, '');
  const currentValue = task[field];

  if (typeof currentValue !== 'string' || !normalizedValue) return;

  task[field] = `${currentValue} ${normalizedValue}`;
};

const isTaskOutroLine = (line: string) =>
  /^notes?:?$/i.test(normalizeAnswerLine(line)) ||
  /^notes?:\s+/i.test(normalizeAnswerLine(line));

export const parseAiMonitorTaskAnswer = (
  content: string
): AiMonitorTaskAnswer | null => {
  const lines = content.split(/\r?\n/);
  const intro: string[] = [];
  const outro: string[] = [];
  const tasks: AiMonitorTaskResult[] = [];
  let currentTask: AiMonitorTaskResult | null = null;
  let lastTaskField: AiMonitorTaskTextField | null = null;
  let foundTask = false;
  let foundOutro = false;

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) return;

    if (foundOutro) {
      outro.push(line);
      return;
    }

    const taskId = parseAiMonitorTaskHeader(line);

    if (taskId || isTaskPlaceholderHeader(line)) {
      if (currentTask) {
        tasks.push(currentTask);
      }

      currentTask = { id: taskId ?? '' };
      lastTaskField = null;
      foundTask = true;
      return;
    }

    const detailMatch = normalizeAnswerLine(line).match(/^([^:]+):\s*(.+)$/);

    if (!foundTask) {
      const introLooksLikeTask = intro.some((introLine) =>
        /\btasks?\b/i.test(introLine)
      );

      if (
        introLooksLikeTask &&
        detailMatch &&
        isTaskDetailLabel(detailMatch[1])
      ) {
        const [, label, value] = detailMatch;

        currentTask = { id: '' };
        lastTaskField = setTaskField(currentTask, label, value);
        foundTask = true;
        return;
      }

      intro.push(line);
      return;
    }

    if (
      currentTask &&
      detailMatch &&
      !foundOutro &&
      isTaskDetailLabel(detailMatch[1])
    ) {
      const [, label, value] = detailMatch;
      lastTaskField = setTaskField(currentTask, label, value);
      return;
    }

    if (currentTask && !isTaskOutroLine(line)) {
      if (lastTaskField) {
        appendTaskField(currentTask, lastTaskField, line);
      }
      return;
    }

    if (currentTask) {
      tasks.push(currentTask);
      currentTask = null;
      lastTaskField = null;
    }

    foundOutro = true;
    outro.push(line);
  });

  if (currentTask) {
    tasks.push(currentTask);
  }

  return tasks.length > 0 ? { intro, tasks, outro } : null;
};

const parseAiMonitorPaymentHeader = (
  line: string
): AiMonitorPaymentHeader | null => {
  const normalizedLine = normalizeAnswerLine(line);
  const paymentMatch = normalizedLine.match(
    /^(Payment|Transaction)\s+#?([A-Za-z0-9-]*\d[A-Za-z0-9-]*)\b(?:\s*\|\s*(.+))?/i
  );

  if (!paymentMatch) return null;

  return {
    type:
      paymentMatch[1].toLowerCase() === 'payment'
        ? ('Payment' as const)
        : ('Transaction' as const),
    id: paymentMatch[2],
    rawDetails: paymentMatch[3],
  };
};

const PAYMENT_DETAIL_LABELS = new Set([
  'client',
  'amount',
  'booking',
  'booking id',
  'booking session',
  'booking session id',
  'booking session date',
  'booking session link',
  'session',
  'session id',
  'session date',
  'session link',
  'failure reason',
  'reason',
  'attempt date',
  'attempted at',
  'attempt',
  'date',
  'status',
  'source link',
  'source',
  'next collection step',
  'next step',
]);

const isPaymentDetailLabel = (label: string) =>
  PAYMENT_DETAIL_LABELS.has(label.toLowerCase().trim());

const setPaymentField = (
  payment: AiMonitorPaymentResult,
  label: string,
  value: string
) => {
  const normalizedLabel = label.toLowerCase().trim();
  const normalizedValue = normalizeValue(value).replace(/\.$/, '');

  if (normalizedLabel === 'client') {
    payment.client = normalizedValue;
    return;
  }

  if (normalizedLabel === 'amount') {
    payment.amount = normalizedValue;
    return;
  }

  if (normalizedLabel === 'booking' || normalizedLabel === 'booking id') {
    payment.bookingId =
      getFirstNumericValue(normalizedValue) || normalizedValue;
    return;
  }

  if (
    normalizedLabel === 'booking session' ||
    normalizedLabel === 'booking session id' ||
    normalizedLabel === 'session' ||
    normalizedLabel === 'session id'
  ) {
    payment.bookingSessionId =
      getFirstNumericValue(normalizedValue) || normalizedValue;
    return;
  }

  if (
    normalizedLabel === 'booking session date' ||
    normalizedLabel === 'session date'
  ) {
    payment.bookingSessionDate = normalizedValue;
    return;
  }

  if (normalizedLabel === 'failure reason' || normalizedLabel === 'reason') {
    payment.failureReason = normalizedValue;
    return;
  }

  if (
    normalizedLabel === 'attempt date' ||
    normalizedLabel === 'attempted at' ||
    normalizedLabel === 'attempt' ||
    normalizedLabel === 'date'
  ) {
    payment.attemptDate = normalizedValue;
    return;
  }

  if (normalizedLabel === 'status') {
    payment.status = normalizedValue;
    return;
  }

  if (
    normalizedLabel === 'booking session link' ||
    normalizedLabel === 'session link'
  ) {
    payment.bookingSessionUrl = normalizedValue;
    return;
  }

  if (normalizedLabel === 'source link' || normalizedLabel === 'source') {
    if (isBookingSessionUrl(normalizedValue)) {
      payment.bookingSessionUrl = normalizedValue;
      return;
    }

    payment.sourceUrl = normalizedValue;
    return;
  }

  if (
    normalizedLabel === 'next collection step' ||
    normalizedLabel === 'next step'
  ) {
    payment.nextCollectionStep = normalizedValue;
  }
};

const setPipeSeparatedPaymentFields = (
  payment: AiMonitorPaymentResult,
  rawDetails?: string
) => {
  if (!rawDetails) return;

  rawDetails
    .split('|')
    .map((detail) => detail.trim())
    .filter(Boolean)
    .forEach((detail) => {
      const detailMatch = detail.match(/^([^:]+):\s*(.+)$/);

      if (!detailMatch || !isPaymentDetailLabel(detailMatch[1])) return;

      const [, label, value] = detailMatch;
      setPaymentField(payment, label, value);
    });
};

export const parseAiMonitorPaymentAnswer = (
  content: string
): AiMonitorPaymentAnswer | null => {
  const lines = content.split(/\r?\n/);
  const intro: string[] = [];
  const outro: string[] = [];
  const payments: AiMonitorPaymentResult[] = [];
  let currentPayment: AiMonitorPaymentResult | null = null;
  let foundPayment = false;
  let foundOutro = false;

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) return;

    const paymentHeader = parseAiMonitorPaymentHeader(line);

    if (paymentHeader) {
      if (currentPayment) {
        payments.push(currentPayment);
      }

      currentPayment = {
        id: paymentHeader.id,
        type: paymentHeader.type,
      };
      setPipeSeparatedPaymentFields(currentPayment, paymentHeader.rawDetails);
      foundPayment = true;
      return;
    }

    if (!foundPayment) {
      intro.push(line);
      return;
    }

    const detailMatch = normalizeAnswerLine(line).match(/^([^:]+):\s*(.+)$/);

    if (
      currentPayment &&
      detailMatch &&
      !foundOutro &&
      isPaymentDetailLabel(detailMatch[1])
    ) {
      const [, label, value] = detailMatch;
      setPaymentField(currentPayment, label, value);
      return;
    }

    if (currentPayment) {
      payments.push(currentPayment);
      currentPayment = null;
    }

    foundOutro = true;
    outro.push(line);
  });

  if (currentPayment) {
    payments.push(currentPayment);
  }

  return payments.length > 0 ? { intro, payments, outro } : null;
};

const ACTION_QUEUE_SEVERITY_PATTERN =
  '(critical|urgent|high|medium-high|medium|normal|low)';

const parseAiMonitorActionHeader = (
  line: string,
  allowGenericNumberedAction: boolean
) => {
  const rawLine = line.trim();
  const numberedMatch = rawLine.match(/^\d+[.)]\s+(.+)$/);
  const normalizedLine = normalizeAnswerLine(line);
  const priorityMatch = normalizedLine.match(
    new RegExp(
      `^${ACTION_QUEUE_SEVERITY_PATTERN}\\s+(?:priority|severity)\\s*:\\s*(.+)$`,
      'i'
    )
  );

  if (priorityMatch) {
    return {
      severity: normalizeValue(priorityMatch[1]),
      title: normalizeValue(priorityMatch[2]),
    };
  }

  if (allowGenericNumberedAction && numberedMatch) {
    const title = normalizeValue(numberedMatch[1]);
    const parentheticalSeverityMatch = title.match(
      new RegExp(
        `^(.*?)\\s*\\(\\s*${ACTION_QUEUE_SEVERITY_PATTERN}\\s*\\)$`,
        'i'
      )
    );

    if (
      !parseAiMonitorPaymentHeader(title) &&
      !parseAiMonitorTaskHeader(title)
    ) {
      if (parentheticalSeverityMatch) {
        return {
          title: normalizeValue(parentheticalSeverityMatch[1]),
          severity: normalizeValue(parentheticalSeverityMatch[2]),
        };
      }

      return { title };
    }
  }

  return null;
};

const ACTION_QUEUE_DETAIL_LABELS = new Set([
  'severity',
  'reason',
  'linked source',
  'source',
  'suggested next step',
  'next step',
]);

const isActionQueueDetailLabel = (label: string) =>
  ACTION_QUEUE_DETAIL_LABELS.has(label.toLowerCase().trim());

const setActionQueueField = (
  action: AiMonitorActionQueueItem,
  label: string,
  value: string
): AiMonitorActionQueueTextField | null => {
  const normalizedLabel = label.toLowerCase().trim();
  const normalizedValue = normalizeValue(value).replace(/\.$/, '');

  if (normalizedLabel === 'severity') {
    action.severity = normalizedValue;
    return 'severity';
  }

  if (normalizedLabel === 'reason') {
    action.reason = normalizedValue;
    return 'reason';
  }

  if (normalizedLabel === 'linked source' || normalizedLabel === 'source') {
    action.linkedSource = normalizedValue;
    return 'linkedSource';
  }

  if (
    normalizedLabel === 'suggested next step' ||
    normalizedLabel === 'next step'
  ) {
    action.nextStep = normalizedValue;
    return 'nextStep';
  }

  return null;
};

const appendActionQueueField = (
  action: AiMonitorActionQueueItem,
  field: AiMonitorActionQueueTextField,
  value: string
) => {
  const normalizedValue = normalizeAnswerLine(value).replace(/\.$/, '');
  const currentValue = action[field];

  if (typeof currentValue !== 'string' || !normalizedValue) return;

  action[field] = `${currentValue} ${normalizedValue}`;
};

const isActionQueueOutroLine = (line: string) =>
  /^notes?:?$/i.test(normalizeAnswerLine(line)) ||
  /^notes?:\s+/i.test(normalizeAnswerLine(line));

export const parseAiMonitorActionQueueAnswer = (
  content: string
): AiMonitorActionQueueAnswer | null => {
  const lines = content.split(/\r?\n/);
  const intro: string[] = [];
  const outro: string[] = [];
  const actions: AiMonitorActionQueueItem[] = [];
  let currentAction: AiMonitorActionQueueItem | null = null;
  let lastActionField: AiMonitorActionQueueTextField | null = null;
  let foundAction = false;
  let foundOutro = false;

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) return;

    if (foundOutro) {
      outro.push(line);
      return;
    }

    const introText = intro.join(' ').toLowerCase();
    const allowGenericNumberedAction =
      introText.includes('action queue') ||
      introText.includes('needs attention');
    const actionHeader = parseAiMonitorActionHeader(
      line,
      allowGenericNumberedAction
    );

    if (actionHeader && !foundOutro) {
      if (currentAction) {
        actions.push(currentAction);
      }

      currentAction = {
        id: String(actions.length + 1),
        title: actionHeader.title,
        severity: actionHeader.severity,
      };
      lastActionField = null;
      foundAction = true;
      return;
    }

    if (!foundAction) {
      intro.push(line);
      return;
    }

    const detailMatch = normalizeAnswerLine(line).match(/^([^:]+):\s*(.+)$/);

    if (
      currentAction &&
      detailMatch &&
      !foundOutro &&
      isActionQueueDetailLabel(detailMatch[1])
    ) {
      const [, label, value] = detailMatch;
      lastActionField = setActionQueueField(currentAction, label, value);
      return;
    }

    if (currentAction && !isActionQueueOutroLine(line)) {
      if (lastActionField) {
        appendActionQueueField(currentAction, lastActionField, line);
      }
      return;
    }

    if (currentAction) {
      actions.push(currentAction);
      currentAction = null;
      lastActionField = null;
    }

    foundOutro = true;
    outro.push(line);
  });

  if (currentAction) {
    actions.push(currentAction);
  }

  return actions.length > 0 ? { intro, actions, outro } : null;
};

const parseAiMonitorSessionLine = (
  line: string
): AiMonitorSessionResult | null => {
  const normalizedLine = normalizeAnswerLine(line);
  const sessionMatch = normalizedLine.match(
    /^Session(?:\s+ID)?\s*:?\s*#?(\d+)\s*\|\s*(.+)$/i
  );

  if (!sessionMatch) return null;

  const [, sessionId, rawDetails] = sessionMatch;
  const details = rawDetails.split('|').map(normalizeValue).filter(Boolean);
  const session: AiMonitorSessionResult = { id: sessionId };

  details.forEach((detail) => {
    const labelledDetail = detail.match(/^([^:]+):\s*(.+)$/);
    const label = labelledDetail?.[1].trim().toLowerCase();
    const value = labelledDetail?.[2] ? normalizeValue(labelledDetail[2]) : '';
    const bookingMatch = detail.match(/^Booking(?:\s+ID)?\s*:?\s*#?(\d+)/i);

    if (bookingMatch) {
      session.bookingId = bookingMatch[1];
      return;
    }

    if (label === 'client') {
      session.client = value;
      return;
    }

    if (label === 'address') {
      session.address = value;
      return;
    }

    if (label === 'service') {
      session.service = value;
      return;
    }

    if (label === 'status') {
      session.status = value.replace(/\.$/, '');
      return;
    }

    if ((label === 'date' || label === 'session date') && value) {
      session.date = value;
      return;
    }

    if (label === 'time' && value) {
      session.time = value;
      return;
    }

    if (!session.date && /^\d{4}-\d{2}-\d{2}$/.test(detail)) {
      session.date = detail;
      return;
    }

    if (!session.time && /^\d{1,2}:\d{2}/.test(detail)) {
      session.time = detail;
    }
  });

  return session;
};

export const parseAiMonitorSessionAnswer = (
  content: string
): AiMonitorSessionAnswer | null => {
  const lines = content.split(/\r?\n/);
  const intro: string[] = [];
  const outro: string[] = [];
  const sessionLines: string[] = [];
  let foundSession = false;
  let foundOutro = false;

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) return;

    if (parseAiMonitorSessionLine(line)) {
      sessionLines.push(line);
      foundSession = true;
      return;
    }

    if (!foundSession) {
      intro.push(line);
      return;
    }

    if (!foundOutro && line.includes('|') && sessionLines.length > 0) {
      sessionLines[sessionLines.length - 1] = `${
        sessionLines[sessionLines.length - 1]
      } ${line}`;
      return;
    }

    foundOutro = true;
    outro.push(line);
  });

  const sessions = sessionLines
    .map(parseAiMonitorSessionLine)
    .filter((session): session is AiMonitorSessionResult => Boolean(session));

  return sessions.length > 0 ? { intro, sessions, outro } : null;
};

export const hasAiMonitorSessionContent = (content: string) =>
  parseAiMonitorSessionAnswer(normalizeAiMonitorAnswerContent(content)) !==
  null;

export const hasAiMonitorStructuredContent = (content: string) => {
  const normalizedContent = normalizeAiMonitorAnswerContent(content);

  return (
    parseAiMonitorKpiAnswer(normalizedContent) !== null ||
    parseAiMonitorActionQueueAnswer(normalizedContent) !== null ||
    parseAiMonitorPaymentAnswer(normalizedContent) !== null ||
    parseAiMonitorTaskAnswer(normalizedContent) !== null ||
    parseAiMonitorSessionAnswer(normalizedContent) !== null
  );
};

const formatSessionDate = (date?: string) => {
  const match = date?.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return date;

  const [, year, month, day] = match;
  const monthIndex = Number(month) - 1;
  const monthLabel = MONTH_LABELS[monthIndex];

  if (!monthLabel) return date;

  return `${Number(day)} ${monthLabel} ${year}`;
};

const getSessionListTitle = (intro: string[]) => {
  const introText = intro.join(' ').toLowerCase();

  if (introText.includes('unassigned')) return 'Unassigned sessions';
  if (introText.includes('cancelled')) return 'Cancelled sessions';
  if (introText.includes('paused')) return 'Paused sessions';
  if (introText.includes('upcoming')) return 'Upcoming sessions';

  return 'Sessions';
};

const getSessionStatusStyles = (status: string | undefined, theme: Theme) => {
  const normalizedStatus = status?.toLowerCase() ?? '';
  let palette = theme.palette.info;

  if (
    normalizedStatus.includes('cancel') ||
    normalizedStatus.includes('void')
  ) {
    palette = theme.palette.error;
  } else if (
    normalizedStatus.includes('pause') ||
    normalizedStatus.includes('pending')
  ) {
    palette = theme.palette.warning;
  } else if (
    normalizedStatus.includes('complete') ||
    normalizedStatus.includes('done') ||
    normalizedStatus.includes('active')
  ) {
    palette = theme.palette.success;
  }

  return {
    bgcolor: alpha(palette.main, 0.12),
    color: palette.dark,
    fontWeight: 800,
  };
};

const getKpiToneStyles = (tone: AiMonitorKpiTone, theme: Theme) => {
  let palette = theme.palette.info;

  if (tone === 'success') {
    palette = theme.palette.success;
  } else if (tone === 'warning') {
    palette = theme.palette.warning;
  } else if (tone === 'error') {
    palette = theme.palette.error;
  }

  return {
    bgcolor: alpha(palette.main, 0.1),
    borderColor: alpha(palette.main, 0.24),
    color: palette.dark,
  };
};

const getTaskPriorityStyles = (priority: string | undefined, theme: Theme) => {
  const normalizedPriority = priority?.toLowerCase() ?? '';
  let palette = theme.palette.info;

  if (normalizedPriority.includes('low')) {
    return {
      bgcolor: alpha(theme.palette.grey[500], 0.12),
      color: theme.palette.grey[700],
      fontWeight: 800,
    };
  }

  if (normalizedPriority.includes('urgent')) {
    palette = theme.palette.error;
  } else if (normalizedPriority.includes('high')) {
    palette = theme.palette.warning;
  }

  return {
    bgcolor: alpha(palette.main, 0.12),
    color: palette.dark,
    fontWeight: 800,
  };
};

const getPaymentStatusStyles = (status: string | undefined, theme: Theme) => {
  const normalizedStatus = status?.toLowerCase() ?? '';
  let palette = theme.palette.info;

  if (
    normalizedStatus.includes('fail') ||
    normalizedStatus.includes('declin') ||
    normalizedStatus.includes('void')
  ) {
    palette = theme.palette.error;
  } else if (
    normalizedStatus.includes('unpaid') ||
    normalizedStatus.includes('pending') ||
    normalizedStatus.includes('retry')
  ) {
    palette = theme.palette.warning;
  } else if (
    normalizedStatus.includes('paid') ||
    normalizedStatus.includes('success') ||
    normalizedStatus.includes('complete')
  ) {
    palette = theme.palette.success;
  }

  return {
    bgcolor: alpha(palette.main, 0.12),
    color: palette.dark,
    fontWeight: 800,
  };
};

const getActionSeverityStyles = (
  severity: string | undefined,
  theme: Theme
) => {
  const normalizedSeverity = severity?.toLowerCase() ?? '';
  let palette = theme.palette.info;

  if (
    normalizedSeverity.includes('critical') ||
    normalizedSeverity.includes('urgent') ||
    normalizedSeverity.includes('high')
  ) {
    palette = theme.palette.error;
  } else if (
    normalizedSeverity.includes('medium') ||
    normalizedSeverity.includes('normal')
  ) {
    palette = theme.palette.warning;
  } else if (normalizedSeverity.includes('low')) {
    palette = theme.palette.success;
  }

  return {
    bgcolor: alpha(palette.main, 0.12),
    color: palette.dark,
    fontWeight: 800,
  };
};

const getSessionSourceUrl = (
  session: AiMonitorSessionResult,
  sources: AiMonitorSource[]
) =>
  sources.find(
    (source) =>
      source.type === 'booking_session' && String(source.id) === session.id
  )?.url ?? null;

const getTaskSource = (
  task: AiMonitorTaskResult,
  sources: AiMonitorSource[]
) => {
  const taskSources = sources.filter((source) => source.type === 'task');
  const normalizedTaskId = getTaskIdValue(task.id);
  const taskRouteUrl = getTaskRouteUrl(task.id);

  if (task.sourceUrl) {
    const matchingUrlSource = taskSources.find(
      (source) => source.url === task.sourceUrl
    );

    if (matchingUrlSource) {
      const matchingRouteUrl =
        taskRouteUrl ?? getTaskRouteUrl(matchingUrlSource.id);

      return {
        ...matchingUrlSource,
        url: matchingRouteUrl ?? matchingUrlSource.url,
      };
    }

    return {
      type: 'task' as const,
      id: Number(normalizedTaskId) || 0,
      label: task.id ? `Task ${task.id}` : 'Task',
      url: taskRouteUrl ?? task.sourceUrl,
    };
  }

  if (normalizedTaskId) {
    const matchingIdSource = taskSources.find(
      (source) =>
        String(source.id) === normalizedTaskId ||
        source.label.toLowerCase() === `task ${normalizedTaskId}`
    );

    if (matchingIdSource) {
      return {
        ...matchingIdSource,
        url: taskRouteUrl ?? matchingIdSource.url,
      };
    }

    return {
      type: 'task' as const,
      id: Number(normalizedTaskId),
      label: `Task ${normalizedTaskId}`,
      url: taskRouteUrl,
    };
  }

  if (!task.id && taskSources.length === 1) {
    const [taskSource] = taskSources;

    return {
      ...taskSource,
      url: getTaskRouteUrl(taskSource.id) ?? taskSource.url,
    };
  }

  return null;
};

const getTaskDisplayLabel = (
  task: AiMonitorTaskResult,
  source?: AiMonitorSource | null
) => (task.id ? `Task ${task.id}` : source?.label ?? 'Task');

const getPaymentSourceUrl = (
  payment: AiMonitorPaymentResult,
  sources: AiMonitorSource[],
  allowSingleSessionFallback: boolean
) => {
  if (isBookingSessionUrl(payment.bookingSessionUrl)) {
    return payment.bookingSessionUrl ?? null;
  }

  if (isBookingSessionUrl(payment.sourceUrl)) {
    return payment.sourceUrl ?? null;
  }

  const matchingSessionSource = sources.find(
    (source) =>
      source.type === 'booking_session' &&
      payment.bookingSessionId &&
      String(source.id) === payment.bookingSessionId &&
      Boolean(source.url)
  );

  if (matchingSessionSource?.url) return matchingSessionSource.url;

  const transactionSessionUrl = sources.find(
    (source) =>
      source.type === 'transaction' &&
      String(source.id) === payment.id &&
      isBookingSessionUrl(source.url)
  )?.url;

  if (transactionSessionUrl) return transactionSessionUrl;

  if (payment.bookingId && payment.bookingSessionId) {
    const sessionDate = getIsoDateValue(payment.bookingSessionDate);
    const sessionFilter = sessionDate
      ? returnSessionFilterFromDate(sessionDate)
      : 'previous';
    const safeSessionFilter =
      sessionFilter === 'error' ? 'previous' : sessionFilter;

    return `${bookingsManagementPath('view', {
      bookingId: payment.bookingId,
      bookingTabSid: 'booking-sessions',
      sessionFilter: safeSessionFilter,
    })}?sessionID=${payment.bookingSessionId}`;
  }

  if (allowSingleSessionFallback) {
    const sessionSources = sources.filter(
      (source) => source.type === 'booking_session' && Boolean(source.url)
    );

    if (sessionSources.length === 1) return sessionSources[0].url;
  }

  return null;
};

const getActionQueueSourceUrl = (
  action: AiMonitorActionQueueItem,
  sources: AiMonitorSource[]
) => {
  const sourceText = action.linkedSource ?? action.title;
  const directUrl = sourceText.match(/https?:\/\/[^\s]+|\/[^\s]+/)?.[0];

  if (directUrl) return directUrl;

  const sessionId = sourceText.match(/(?:sessionId|session)\s*#?(\d+)/i)?.[1];
  const taskId = sourceText.match(/(?:task|gsc-)\s*#?(\d+)/i)?.[1];
  const bookingId = sourceText.match(/booking\s*#?(\d+)/i)?.[1];

  if (sessionId) {
    const sessionSource = sources.find(
      (source) =>
        source.type === 'booking_session' && String(source.id) === sessionId
    );

    if (sessionSource?.url) return sessionSource.url;
  }

  if (taskId) {
    const taskSource = sources.find(
      (source) => source.type === 'task' && String(source.id) === taskId
    );

    if (taskSource?.url) return taskSource.url;
  }

  if (bookingId) {
    const bookingSource = sources.find(
      (source) => source.type === 'booking' && String(source.id) === bookingId
    );

    if (bookingSource?.url) return bookingSource.url;
  }

  const normalizedSourceText = sourceText.toLowerCase();
  const matchingLabelSource = sources.find((source) => {
    const normalizedLabel = source.label.toLowerCase();

    return (
      Boolean(source.url) &&
      (normalizedSourceText.includes(normalizedLabel) ||
        normalizedLabel.includes(normalizedSourceText))
    );
  });

  if (matchingLabelSource?.url) return matchingLabelSource.url;

  if (normalizedSourceText.includes('dashboard')) {
    return (
      sources.find(
        (source) => source.type === 'dashboard_report' && Boolean(source.url)
      )?.url ?? null
    );
  }

  return null;
};

const getTaskTags = (tags?: string) => {
  if (!tags || /not available|none|n\/a/i.test(tags)) return [];

  return tags
    .split(/[,|]/)
    .map((tag) => tag.trim())
    .filter(Boolean);
};

const getTaskListTitle = (intro: string[]) => {
  const introText = intro.join(' ').toLowerCase();

  if (introText.includes('urgent') || introText.includes('high-priority')) {
    return 'Urgent/high-priority tasks';
  }

  if (introText.includes('open')) return 'Open tasks';

  return 'Tasks';
};

const getPaymentListTitle = (intro: string[]) => {
  const introText = intro.join(' ').toLowerCase();

  if (introText.includes('failed') && introText.includes('unpaid')) {
    return 'Failed/unpaid payments';
  }

  if (introText.includes('failed')) return 'Failed payments';
  if (introText.includes('unpaid')) return 'Unpaid payments';

  return 'Payments';
};

const getActionQueueTitle = (intro: string[]) => {
  const queueLine = intro.find((line) => /action queue/i.test(line));

  return queueLine?.replace(/:$/, '') || 'Action queue';
};

const getPaymentSessionLookupPrompt = (payment: AiMonitorPaymentResult) => {
  const details = [
    `${payment.type} ${payment.id}`,
    payment.client ? `client ${payment.client}` : '',
    payment.amount ? `amount ${payment.amount}` : '',
    payment.bookingId ? `booking ${payment.bookingId}` : '',
  ]
    .filter(Boolean)
    .join(', ');

  return `Find the booking session for ${details}. Return it as a payment card with transaction id, client, amount, booking id, booking session id/link, failure reason, payment status, and attempt date. Link to the booking session, not a transaction page.`;
};

function DetailItem({ icon, label, value }: DetailItemProps) {
  if (!value) return null;

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="flex-start"
      sx={{ minWidth: 0 }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          mt: 0.1,
          flexShrink: 0,
          color: 'text.secondary',
        }}
      >
        <Iconify icon={icon} width={18} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          component="div"
          sx={{
            color: 'text.secondary',
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          component="div"
          sx={{
            color: 'text.primary',
            lineHeight: 1.35,
            wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

function ActionQueueCard({
  action,
  sources,
}: {
  action: AiMonitorActionQueueItem;
  sources: AiMonitorSource[];
}) {
  const sourceUrl = getActionQueueSourceUrl(action, sources);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        p: 1.25,
      }}
    >
      <Stack spacing={1.15}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={0.85} sx={{ minWidth: 0 }}>
            <Box
              sx={(theme) => ({
                width: 24,
                height: 24,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
                fontWeight: 800,
                fontSize: 13,
              })}
            >
              {action.id}
            </Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, lineHeight: 1.35, minWidth: 0 }}
            >
              {action.title}
            </Typography>
          </Stack>

          {action.severity && (
            <Chip
              size="small"
              label={action.severity}
              sx={(theme) => ({
                borderRadius: 0.75,
                flexShrink: 0,
                ...getActionSeverityStyles(action.severity, theme),
              })}
            />
          )}
        </Stack>

        <DetailItem
          icon="eva:alert-circle-outline"
          label="Reason"
          value={action.reason}
        />

        {action.linkedSource && (
          <Stack spacing={0.45}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 700 }}
            >
              Linked source
            </Typography>
            {sourceUrl ? (
              <Link
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{
                  alignSelf: 'flex-start',
                  fontWeight: 800,
                  textDecoration: 'none',
                  wordBreak: 'break-word',
                }}
              >
                {action.linkedSource}
              </Link>
            ) : (
              <Typography
                variant="body2"
                sx={{ wordBreak: 'break-word', lineHeight: 1.35 }}
              >
                {action.linkedSource}
              </Typography>
            )}
          </Stack>
        )}

        {action.nextStep && (
          <Box
            sx={{
              borderRadius: 1,
              bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
              px: 1,
              py: 0.85,
            }}
          >
            <DetailItem
              icon="eva:arrow-forward-circle-outline"
              label="Suggested next step"
              value={action.nextStep}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
}

function ActionQueueAnswer({
  answer,
  sources,
}: {
  answer: AiMonitorActionQueueAnswer;
  sources: AiMonitorSource[];
}) {
  const countLabel = `${answer.actions.length} ${
    answer.actions.length === 1 ? 'action' : 'actions'
  }`;

  return (
    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.warning.main, 0.24),
          borderRadius: 1,
          bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06),
          px: 1.25,
          py: 1,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Iconify icon="eva:alert-triangle-outline" width={18} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {getActionQueueTitle(answer.intro)}
            </Typography>
          </Stack>
          <Chip
            size="small"
            label={countLabel}
            sx={{ borderRadius: 0.75, fontWeight: 800 }}
          />
        </Stack>
      </Box>

      <Stack spacing={1}>
        {answer.actions.map((action) => (
          <ActionQueueCard
            key={`${action.id}-${action.title}`}
            action={action}
            sources={sources}
          />
        ))}
      </Stack>

      {answer.outro.map((line) => (
        <Typography key={line} variant="body2" sx={{ lineHeight: 1.6 }}>
          {line}
        </Typography>
      ))}
    </Stack>
  );
}

function PaymentCard({
  payment,
  sources,
  allowSingleSessionFallback,
  inputDisabled,
  onSubmitPrompt,
}: {
  payment: AiMonitorPaymentResult;
  sources: AiMonitorSource[];
  allowSingleSessionFallback: boolean;
  inputDisabled?: boolean;
  onSubmitPrompt?: (prompt: string) => void;
}) {
  const sourceUrl = getPaymentSourceUrl(
    payment,
    sources,
    allowSingleSessionFallback
  );
  const canRequestSession = Boolean(onSubmitPrompt) && !inputDisabled;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        p: 1.25,
      }}
    >
      <Stack spacing={1.15}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            {sourceUrl ? (
              <Link
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="subtitle2"
                sx={{ fontWeight: 800, textDecoration: 'none' }}
              >
                {payment.type} {payment.id}
              </Link>
            ) : (
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {payment.type} {payment.id}
              </Typography>
            )}

            {payment.bookingId && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Booking #{payment.bookingId}
              </Typography>
            )}
          </Stack>

          {payment.status && (
            <Chip
              size="small"
              label={payment.status}
              sx={(theme) => ({
                borderRadius: 0.75,
                flexShrink: 0,
                ...getPaymentStatusStyles(payment.status, theme),
              })}
            />
          )}
        </Stack>

        {payment.amount && (
          <Box
            sx={{
              borderRadius: 1,
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
              px: 1,
              py: 0.85,
            }}
          >
            <Typography
              variant="caption"
              component="div"
              sx={{ color: 'text.secondary', fontWeight: 700, lineHeight: 1.2 }}
            >
              Amount
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
              {payment.amount}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'minmax(0, 1fr) minmax(0, 1fr)',
            },
            gap: 1,
          }}
        >
          <DetailItem
            icon="eva:person-outline"
            label="Client"
            value={payment.client}
          />
          <DetailItem
            icon="eva:briefcase-outline"
            label="Booking"
            value={payment.bookingId ? `#${payment.bookingId}` : undefined}
          />
          <DetailItem
            icon="eva:clock-outline"
            label="Booking session"
            value={
              payment.bookingSessionId
                ? `#${payment.bookingSessionId}`
                : undefined
            }
          />
          <DetailItem
            icon="eva:calendar-outline"
            label="Attempt date"
            value={payment.attemptDate}
          />
          <DetailItem
            icon="eva:alert-circle-outline"
            label="Failure reason"
            value={payment.failureReason}
          />
          <DetailItem
            icon="eva:arrow-forward-circle-outline"
            label="Next collection step"
            value={payment.nextCollectionStep}
          />
        </Box>

        {!sourceUrl && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: (theme) => alpha(theme.palette.warning.main, 0.2),
              borderRadius: 1,
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06),
              px: 1,
              py: 0.85,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
            >
              <DetailItem
                icon="eva:link-2-outline"
                label="Booking session"
                value="Not available in this answer"
              />

              <Button
                size="small"
                variant="outlined"
                startIcon={<Iconify icon="eva:search-outline" width={16} />}
                onClick={() =>
                  onSubmitPrompt?.(getPaymentSessionLookupPrompt(payment))
                }
                disabled={!canRequestSession}
                sx={{ borderRadius: 1, flexShrink: 0 }}
              >
                Find booking session
              </Button>
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

function PaymentAnswer({
  answer,
  sources,
  inputDisabled,
  onSubmitPrompt,
}: {
  answer: AiMonitorPaymentAnswer;
  sources: AiMonitorSource[];
  inputDisabled?: boolean;
  onSubmitPrompt?: (prompt: string) => void;
}) {
  const countLabel = `${answer.payments.length} ${
    answer.payments.length === 1 ? 'payment' : 'payments'
  }`;

  return (
    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.error.main, 0.22),
          borderRadius: 1,
          bgcolor: (theme) => alpha(theme.palette.error.main, 0.05),
          px: 1.25,
          py: 1,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Iconify icon="eva:credit-card-outline" width={18} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {getPaymentListTitle(answer.intro)}
            </Typography>
          </Stack>
          <Chip
            size="small"
            label={countLabel}
            sx={{ borderRadius: 0.75, fontWeight: 800 }}
          />
        </Stack>
      </Box>

      <Stack spacing={1}>
        {answer.payments.map((payment) => (
          <PaymentCard
            key={`${payment.type}-${payment.id}`}
            payment={payment}
            sources={sources}
            allowSingleSessionFallback={answer.payments.length === 1}
            inputDisabled={inputDisabled}
            onSubmitPrompt={onSubmitPrompt}
          />
        ))}
      </Stack>

      {answer.outro.map((line) => (
        <Typography key={line} variant="body2" sx={{ lineHeight: 1.6 }}>
          {line}
        </Typography>
      ))}
    </Stack>
  );
}

function TaskCard({
  task,
  sources,
}: {
  task: AiMonitorTaskResult;
  sources: AiMonitorSource[];
}) {
  const taskSource = getTaskSource(task, sources);
  const sourceUrl = taskSource?.url ?? null;
  const taskLabel = getTaskDisplayLabel(task, taskSource);
  const tags = getTaskTags(task.tags);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        p: 1.25,
      }}
    >
      <Stack spacing={1.15}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            {sourceUrl ? (
              <Link
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="subtitle2"
                sx={{ fontWeight: 800, textDecoration: 'none' }}
              >
                {taskLabel}
              </Link>
            ) : (
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {taskLabel}
              </Typography>
            )}

            {task.title && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.35,
                  wordBreak: 'break-word',
                }}
              >
                {task.title}
              </Typography>
            )}

            {task.section && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {task.section}
              </Typography>
            )}
          </Stack>

          {task.priority && (
            <Chip
              size="small"
              label={task.priority}
              sx={(theme) => ({
                borderRadius: 0.75,
                flexShrink: 0,
                ...getTaskPriorityStyles(task.priority, theme),
              })}
            />
          )}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'minmax(0, 1fr) minmax(0, 1fr)',
            },
            gap: 1,
          }}
        >
          <DetailItem
            icon="eva:person-outline"
            label="Assignee"
            value={task.assignee}
          />
          <DetailItem
            icon="eva:calendar-outline"
            label="Due date"
            value={task.dueDate}
          />
          <DetailItem
            icon="eva:layout-outline"
            label="Section"
            value={task.section}
          />
          {tags.length === 0 && (
            <DetailItem
              icon="eva:pricetags-outline"
              label="Tags"
              value={task.tags}
            />
          )}
        </Box>

        {tags.length > 0 && (
          <Stack spacing={0.5}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 700 }}
            >
              Tags
            </Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  size="small"
                  label={tag}
                  variant="outlined"
                  sx={{ borderRadius: 0.75, fontWeight: 700 }}
                />
              ))}
            </Stack>
          </Stack>
        )}

        {task.nextAction && (
          <Box
            sx={{
              borderRadius: 1,
              bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
              px: 1,
              py: 0.85,
            }}
          >
            <DetailItem
              icon="eva:arrow-forward-circle-outline"
              label="Next action"
              value={task.nextAction}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
}

function TaskAnswer({
  answer,
  sources,
}: {
  answer: AiMonitorTaskAnswer;
  sources: AiMonitorSource[];
}) {
  const countLabel = `${answer.tasks.length} ${
    answer.tasks.length === 1 ? 'task' : 'tasks'
  }`;

  return (
    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.warning.main, 0.24),
          borderRadius: 1,
          bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06),
          px: 1.25,
          py: 1,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Iconify icon="eva:checkmark-square-2-outline" width={18} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 800, minWidth: 0 }}
            >
              {getTaskListTitle(answer.intro)}
            </Typography>
          </Stack>
          <Chip
            size="small"
            label={countLabel}
            sx={{ borderRadius: 0.75, flexShrink: 0, fontWeight: 800 }}
          />
        </Stack>
      </Box>

      <Stack spacing={1}>
        {answer.tasks.map((task, index) => (
          <TaskCard
            key={`${task.id || 'task'}-${index}`}
            task={task}
            sources={sources}
          />
        ))}
      </Stack>

      {answer.outro.map((line) => {
        const normalizedLine = normalizeAnswerLine(line);

        return (
          <Typography
            key={line}
            variant="body2"
            sx={{ lineHeight: 1.6, wordBreak: 'break-word' }}
          >
            {normalizedLine}
          </Typography>
        );
      })}
    </Stack>
  );
}

function KpiSummaryStrip({ metrics }: { metrics: AiMonitorKpiMetric[] }) {
  return (
    <Stack spacing={0.75} aria-label="KPI summary">
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Iconify icon="eva:bar-chart-2-outline" width={18} />
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          KPI summary
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(auto-fit, minmax(118px, 1fr))',
          },
          gap: 1,
        }}
      >
        {metrics.map((metric) => (
          <Box
            key={metric.key}
            sx={(theme) => ({
              border: '1px solid',
              borderRadius: 1,
              p: 1,
              minWidth: 0,
              ...getKpiToneStyles(metric.tone, theme),
            })}
          >
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Iconify icon={metric.icon} width={16} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {metric.label}
                </Typography>
              </Stack>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.1,
                  wordBreak: 'break-word',
                }}
              >
                {metric.value}
              </Typography>
            </Stack>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

function KpiAnswer({
  answer,
  sources,
  inputDisabled,
  onSubmitPrompt,
}: {
  answer: AiMonitorKpiAnswer;
  sources: AiMonitorSource[];
  inputDisabled?: boolean;
  onSubmitPrompt?: (prompt: string) => void;
}) {
  const actionQueueAnswer = parseAiMonitorActionQueueAnswer(answer.body);
  const paymentAnswer = parseAiMonitorPaymentAnswer(answer.body);
  const taskAnswer = parseAiMonitorTaskAnswer(answer.body);
  const sessionAnswer = parseAiMonitorSessionAnswer(answer.body);

  return (
    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
      <KpiSummaryStrip metrics={answer.metrics} />

      {actionQueueAnswer ? (
        <ActionQueueAnswer answer={actionQueueAnswer} sources={sources} />
      ) : paymentAnswer ? (
        <PaymentAnswer
          answer={paymentAnswer}
          sources={sources}
          inputDisabled={inputDisabled}
          onSubmitPrompt={onSubmitPrompt}
        />
      ) : taskAnswer ? (
        <TaskAnswer answer={taskAnswer} sources={sources} />
      ) : sessionAnswer ? (
        <SessionAnswer answer={sessionAnswer} sources={sources} />
      ) : (
        answer.body && <PlainTextAnswer content={answer.body} />
      )}
    </Stack>
  );
}

function SessionCard({
  session,
  sources,
}: {
  session: AiMonitorSessionResult;
  sources: AiMonitorSource[];
}) {
  const sourceUrl = getSessionSourceUrl(session, sources);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        p: 1.25,
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            {sourceUrl ? (
              <Link
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="subtitle2"
                sx={{ fontWeight: 800, textDecoration: 'none' }}
              >
                Session #{session.id}
              </Link>
            ) : (
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Session #{session.id}
              </Typography>
            )}

            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Booking #{session.bookingId ?? 'N/A'}
            </Typography>
          </Stack>

          {session.status && (
            <Chip
              size="small"
              label={session.status}
              sx={(theme) => ({
                borderRadius: 0.75,
                flexShrink: 0,
                ...getSessionStatusStyles(session.status, theme),
              })}
            />
          )}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'minmax(0, 0.9fr) minmax(0, 1fr)',
            },
            gap: 1,
          }}
        >
          <DetailItem
            icon="eva:calendar-outline"
            label="Date"
            value={formatSessionDate(session.date)}
          />
          <DetailItem
            icon="eva:clock-outline"
            label="Time"
            value={session.time}
          />
          <DetailItem
            icon="eva:person-outline"
            label="Client"
            value={session.client}
          />
          <DetailItem
            icon="eva:briefcase-outline"
            label="Service"
            value={session.service}
          />
        </Box>

        <DetailItem
          icon="eva:pin-outline"
          label="Address"
          value={session.address}
        />
      </Stack>
    </Box>
  );
}

function SessionAnswer({
  answer,
  sources,
}: {
  answer: AiMonitorSessionAnswer;
  sources: AiMonitorSource[];
}) {
  return (
    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
      {answer.intro.map((line) => (
        <Typography key={line} variant="body2" sx={{ lineHeight: 1.6 }}>
          {line}
        </Typography>
      ))}

      <Box
        sx={{
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
          borderRadius: 1,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
          px: 1.25,
          py: 1,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Iconify icon="eva:list-outline" width={18} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {getSessionListTitle(answer.intro)}
            </Typography>
          </Stack>
          <Chip
            size="small"
            label={`${answer.sessions.length} sessions`}
            sx={{ borderRadius: 0.75, fontWeight: 800 }}
          />
        </Stack>
      </Box>

      <Stack spacing={1}>
        {answer.sessions.map((session) => (
          <SessionCard key={session.id} session={session} sources={sources} />
        ))}
      </Stack>

      {answer.outro.map((line) => (
        <Typography key={line} variant="body2" sx={{ lineHeight: 1.6 }}>
          {line}
        </Typography>
      ))}
    </Stack>
  );
}

function PlainTextAnswer({ content }: { content: string }) {
  return (
    <Typography
      variant="body2"
      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
    >
      {content}
    </Typography>
  );
}

export default function AiMonitorMessageContent({
  content,
  sources = [],
  inputDisabled,
  onSubmitPrompt,
}: AiMonitorMessageContentProps) {
  const normalizedContent = useMemo(
    () => normalizeAiMonitorAnswerContent(content),
    [content]
  );
  const kpiAnswer = useMemo(
    () => parseAiMonitorKpiAnswer(normalizedContent),
    [normalizedContent]
  );
  const actionQueueAnswer = useMemo(
    () => parseAiMonitorActionQueueAnswer(normalizedContent),
    [normalizedContent]
  );
  const paymentAnswer = useMemo(
    () => parseAiMonitorPaymentAnswer(normalizedContent),
    [normalizedContent]
  );
  const taskAnswer = useMemo(
    () => parseAiMonitorTaskAnswer(normalizedContent),
    [normalizedContent]
  );
  const sessionAnswer = useMemo(
    () => parseAiMonitorSessionAnswer(normalizedContent),
    [normalizedContent]
  );

  if (kpiAnswer) {
    return (
      <KpiAnswer
        answer={kpiAnswer}
        sources={sources}
        inputDisabled={inputDisabled}
        onSubmitPrompt={onSubmitPrompt}
      />
    );
  }

  if (actionQueueAnswer) {
    return <ActionQueueAnswer answer={actionQueueAnswer} sources={sources} />;
  }

  if (paymentAnswer) {
    return (
      <PaymentAnswer
        answer={paymentAnswer}
        sources={sources}
        inputDisabled={inputDisabled}
        onSubmitPrompt={onSubmitPrompt}
      />
    );
  }

  if (taskAnswer) {
    return <TaskAnswer answer={taskAnswer} sources={sources} />;
  }

  if (sessionAnswer) {
    return <SessionAnswer answer={sessionAnswer} sources={sources} />;
  }

  return <PlainTextAnswer content={normalizedContent} />;
}
